/**
 * Onglet Classements
 *
 * Affiche les classements joueurs pour les modes Détente et Compétition.
 * Sidebar gauche : catégories de classement (adaptées au mode).
 * Zone principale : tableau avec infinite scroll et recherche par pseudo.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { PlayerAvatar } from '../components/PlayerAvatar';
import ClickablePseudo from '../components/ClickablePseudo';
import MasterDetail from '../components/MasterDetail';
import {
  LiveBroadcastIcon,
  StarIcon,
  ChartTrendIcon,
  CalendarIcon,
  TargetIcon,
  FlameIcon,
  CheckCircleIcon,
  MedalIcon,
  PersonIcon,
  GroupIcon,
  MixteIcon,
  MaxiIcon,
  MaxiAttackIcon,
  AttackIcon,
  AttackSurContratIcon,
  DefenseIcon,
  MaxiDefenseIcon,
  NormalIcon,
} from '../components/Icons';
import type { Session } from '../types/app';
import { useTabReselect } from '../hooks/useTabReselect';
import '../styles/rankings.css';
import { BACKEND_URL } from '../config';

const PAGE_SIZE = 50;

// ============================================
// Types
// ============================================

interface RankingItem {
  pseudo: string;
  pseudo_display?: string;
  score?: number;
  scores?: number[];
  top9?: boolean[];
  [key: string]: unknown;
}

interface DiffInfo {
  id: number;
  fin: string;
}

interface RankingData {
  items: RankingItem[];
  count: number;
  total?: number;
  diffs?: DiffInfo[];
  mode?: string;
  limit?: number;
  offset?: number;
  type?: string;
}

interface ColumnDef {
  key: string;
  label: string;
  width?: number;
  format?: (value: unknown, item?: RankingItem) => React.ReactNode;
}

interface CategoryDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  group: string;
  endpoint: string;
  columns: ColumnDef[];
  subOptions?: { value: string; label: string; icon?: React.ReactNode }[];
  subParam?: string;
  defaultSub?: string;
  hasGlissant?: boolean;
}

// ============================================
// Formatters
// ============================================

const fmtNum = (v: unknown) => {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  return isNaN(n) ? String(v) : n.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
};

/** Render a number with the decimal part in smaller muted text + comma separator */
const fmtPctRich = (v: unknown): React.ReactNode => {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  const fixed = n.toFixed(2);
  const [int, dec] = fixed.split('.');
  return <>{int}<span className="rankings-decimal">,{dec}%</span></>;
};

const fmtPct = (v: unknown): React.ReactNode => {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  const fixed = n.toFixed(1);
  const [int, dec] = fixed.split('.');
  return <>{int}<span className="rankings-decimal">,{dec}%</span></>;
};

const fmtTop5 = (_v: unknown, item?: RankingItem): React.ReactNode => {
  if (!item) return '-';
  const t = Number(item.top5);
  const n = Number(item.nb);
  if (isNaN(t) || isNaN(n)) return '-';
  return <><span className="rankings-top5-num">{t}</span><span className="rankings-decimal"> / {n}</span></>;
};

const fmtScore = (v: unknown): React.ReactNode => {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  const fixed = n.toFixed(1);
  const [int, dec] = fixed.split('.');
  return <>{int}<span className="rankings-decimal">,{dec}</span></>;
};

// ============================================
// Catégories — Mode Détente
// ============================================

const MODE_345: CategoryDef['subOptions'] = [
  { value: '3', label: '3 j.' },
  { value: '4', label: '4 j.' },
  { value: '5', label: '5 j.' },
];

const DETENTE_CATEGORIES: CategoryDef[] = [
  {
    id: 'points', label: 'Points', icon: <span style={{ color: '#eab308' }}><StarIcon size={18} /></span>, group: 'Classements généraux',
    endpoint: '/api/rankings/detente/points',
    columns: [{ key: 'points', label: 'Points', format: fmtNum }],
    subOptions: MODE_345, subParam: 'mode', defaultSub: '4',
  },
  {
    id: 'live', label: 'Live', icon: <span style={{ color: '#ef4444' }}><LiveBroadcastIcon size={18} /></span>, group: 'Classements généraux',
    endpoint: '/api/rankings/detente/live',
    columns: [{ key: 'pfinal', label: 'Points', format: fmtPct }],
    subOptions: MODE_345, subParam: 'mode', defaultSub: '4',
  },
  {
    id: 'glissant', label: 'Glissant', icon: <span style={{ color: '#0d9488' }}><ChartTrendIcon size={18} /></span>, group: 'Classements généraux',
    endpoint: '/api/rankings/detente/glissant',
    columns: [{ key: 'moyenne', label: 'Moyenne', format: fmtNum }],
    subOptions: MODE_345, subParam: 'mode', defaultSub: '4',
  },
  {
    id: 'presence', label: 'Présence', icon: <span style={{ color: '#3b82f6' }}><CalendarIcon size={18} /></span>, group: 'Activité',
    endpoint: '/api/rankings/detente/presence',
    columns: [{ key: 'total', label: 'Total', format: fmtNum }],
    subOptions: MODE_345, subParam: 'mode', defaultSub: '4',
  },
  {
    id: 'assiduite', label: 'Assiduité', icon: <span style={{ color: '#8b5cf6' }}><TargetIcon size={18} /></span>, group: 'Activité',
    endpoint: '/api/rankings/detente/assiduite',
    columns: [{ key: 'total_parties', label: 'Parties', format: fmtNum }],
  },
  {
    id: 'audace', label: 'Audace', icon: <span style={{ color: '#f97316' }}><FlameIcon size={18} /></span>, group: 'Performance',
    endpoint: '/api/rankings/detente/audace',
    columns: [
      { key: 'pct_audace', label: '% Audace', format: fmtPct },
      { key: 'total_parties', label: 'Parties', width: 80, format: fmtNum },
    ],
  },
  {
    id: 'reussite', label: 'Réussite', icon: <span style={{ color: '#22c55e' }}><CheckCircleIcon size={18} /></span>, group: 'Performance',
    endpoint: '/api/rankings/detente/reussite',
    columns: [
      { key: 'pct_reussite', label: '% Réussite', format: fmtPct },
      { key: 'audace', label: 'Contrats', width: 80, format: fmtNum },
    ],
  },
];

// ============================================
// Catégories — Mode Compétition
// ============================================

const TYPE_INDIV: CategoryDef['subOptions'] = [
  { value: 'mixte', label: 'Mixte', icon: <MixteIcon size={13} /> },
  { value: 'maxi', label: 'Maxi', icon: <MaxiIcon size={13} /> },
  { value: 'attaqueSC', label: 'Att. SC', icon: <AttackSurContratIcon size={13} /> },
  { value: 'attaque', label: 'Attaque', icon: <AttackIcon size={13} /> },
  { value: 'defense', label: 'Défense', icon: <DefenseIcon size={13} /> },
  { value: 'maxiAtt', label: 'Maxi Att.', icon: <MaxiAttackIcon size={13} /> },
  { value: 'maxiDef', label: 'Maxi Déf.', icon: <MaxiDefenseIcon size={13} /> },
];

const COMPETITION_CATEGORIES: CategoryDef[] = [
  {
    id: 'compet-live', label: 'Live', icon: <span style={{ color: '#ef4444' }}><LiveBroadcastIcon size={18} /></span>, group: 'Classements live',
    endpoint: '/api/rankings/competition/live',
    columns: [
      { key: 'pfinal', label: 'Points', width: 80, format: fmtPctRich },
      { key: 'ptot', label: '%age', width: 72, format: fmtPctRich },
      { key: 'patt', label: 'Att', width: 62, format: fmtPctRich },
      { key: 'pdef', label: 'Déf', width: 62, format: fmtPctRich },
      { key: 'top5', label: 'Top 5', width: 80, format: fmtTop5 },
    ],
  },
  {
    id: 'differe-indiv', label: 'Différé Individuel', icon: <span style={{ color: '#0d9488' }}><PersonIcon size={18} /></span>, group: 'Différé glissant',
    endpoint: '/api/rankings/competition/differe-indiv',
    columns: [
      { key: 'score', label: 'Tot', format: fmtScore },
    ],
    subOptions: TYPE_INDIV, subParam: 'type', defaultSub: 'mixte',
    hasGlissant: true,
  },
  {
    id: 'differe-indiv-general', label: 'Différé Individuel Général', icon: <span style={{ color: '#0d9488' }}><ChartTrendIcon size={18} /></span>, group: 'Différé glissant',
    endpoint: '/api/rankings/competition/differe-indiv-general',
    columns: [
      { key: 'score', label: 'Tot', format: fmtScore },
      { key: 'score_att', label: 'Att', width: 55, format: fmtScore },
      { key: 'score_def', label: 'Déf', width: 55, format: fmtScore },
    ],
    subOptions: [{ value: 'normal', label: 'Normal', icon: <NormalIcon size={13} /> }, { value: 'maxi', label: 'Maxi', icon: <MaxiIcon size={13} /> }],
    subParam: 'mode', defaultSub: 'normal',
  },
  {
    id: 'differe-tripl', label: 'Différé Triplette', icon: <span style={{ color: '#3b82f6' }}><GroupIcon size={18} /></span>, group: 'Différé glissant',
    endpoint: '/api/rankings/competition/differe-tripl',
    columns: [
      { key: 'score', label: 'Tot', format: fmtScore },
      { key: 'score_att', label: 'Att', width: 55, format: fmtScore },
      { key: 'score_def', label: 'Déf', width: 55, format: fmtScore },
    ],
    hasGlissant: true,
  },
  {
    id: 'differe-quad', label: 'Différé Quadrette', icon: <span style={{ color: '#3b82f6' }}><GroupIcon size={18} /></span>, group: 'Différé glissant',
    endpoint: '/api/rankings/competition/differe-quad',
    columns: [
      { key: 'moyenne', label: 'Moyenne', format: fmtNum },
      { key: 'compteur', label: 'Joués', width: 60, format: fmtNum },
    ],
  },
  {
    id: 'triplettes', label: 'Triplettes', icon: <span style={{ color: '#eab308' }}><MedalIcon size={18} /></span>, group: 'Compétitions',
    endpoint: '/api/rankings/competition/triplettes',
    columns: [
      { key: 'total', label: 'Parties', format: fmtNum },
    ],
  },
];

// ============================================
// Composant principal
// ============================================

interface RankingsProps {
  session: Session;
}

export default function Rankings({ session }: RankingsProps) {
  const categories = session.compet ? COMPETITION_CATEGORIES : DETENTE_CATEGORIES;

  const [selectedId, setSelectedId] = useState(categories[0].id);
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  useTabReselect('rankings', () => setShowMobileDetail(false));
  const [subValues, setSubValues] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Infinite scroll state
  const [allItems, setAllItems] = useState<RankingItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [diffs, setDiffs] = useState<DiffInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const meRowRef = useRef<HTMLDivElement>(null);
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const fetchOffsetRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const selected = useMemo(
    () => categories.find((c) => c.id === selectedId) ?? categories[0],
    [categories, selectedId]
  );

  // Reset selection when switching mode
  useEffect(() => {
    setSelectedId(categories[0].id);
    setSubValues({});
    setSearch('');
    setDebouncedSearch('');
  }, [session.compet]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [search]);

  // Reset data when category/sub/search changes
  const resetKey = `${selected.id}|${subValues[selected.id] ?? selected.defaultSub}|${debouncedSearch}`;
  useEffect(() => {
    setAllItems([]);
    setTotalCount(0);
    setDiffs([]);
    setHasMore(true);
    setError(null);
    fetchOffsetRef.current = 0;
  }, [resetKey]);

  // Fetch data (append for infinite scroll)
  const fetchData = useCallback(async (offset: number) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (offset === 0) setInitialLoading(true);
    setLoading(true);
    setError(null);

    const subVal = subValues[selected.id] ?? selected.defaultSub;
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (subVal && selected.subParam) params.set(selected.subParam, subVal);

    try {
      const res = await fetch(`${BACKEND_URL}${selected.endpoint}?${params}`, {
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Erreur inconnue');

      const data = json.data as RankingData;
      const newItems = data.items ?? [];
      const total = data.total ?? data.count ?? 0;

      if (data.diffs) setDiffs(data.diffs);

      if (offset === 0) {
        setAllItems(newItems);
      } else {
        setAllItems((prev) => [...prev, ...newItems]);
      }

      // When total is available, use it; otherwise just check batch size
      if (total > 0) {
        setTotalCount(total);
        setHasMore(offset + newItems.length < total);
      } else {
        setTotalCount(offset + newItems.length);
        setHasMore(newItems.length === PAGE_SIZE);
      }
      fetchOffsetRef.current = offset + newItems.length;
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return;
      setError(e instanceof Error ? e.message : 'Erreur réseau');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [selected, subValues, debouncedSearch]);

  // Initial fetch
  useEffect(() => {
    fetchData(0);
  }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchData(fetchOffsetRef.current);
        }
      },
      { rootMargin: '800px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, fetchData]);

  // Scroll to "me" row after initial data loads
  useEffect(() => {
    if (allItems.length > 0 && !debouncedSearch && meRowRef.current && tableBodyRef.current) {
      const timer = setTimeout(() => {
        meRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [allItems.length, debouncedSearch]);

  const handleCategoryClick = (id: string) => {
    setSelectedId(id);
    setSearch('');
    setDebouncedSearch('');
    setShowMobileDetail(true);
  };

  const handleSubChange = (value: string) => {
    setSubValues((prev) => ({ ...prev, [selected.id]: value }));
  };

  const currentSub = subValues[selected.id] ?? selected.defaultSub;
  const myPseudo = session.pseudo.toLowerCase();

  // Group categories by group label
  const groups = useMemo(() => {
    const map = new Map<string, CategoryDef[]>();
    for (const cat of categories) {
      const list = map.get(cat.group) ?? [];
      list.push(cat);
      map.set(cat.group, list);
    }
    return Array.from(map.entries());
  }, [categories]);

  const getRank = (item: RankingItem, index: number): number => {
    if (typeof item.classement === 'number') return item.classement;
    if (typeof item.rank === 'number') return item.rank;
    if (typeof item.classement_tot === 'number') return item.classement_tot;
    return index + 1;
  };

  const isTeamRanking = selected.id === 'differe-quad';
  const showGlissant = selected.hasGlissant && diffs.length > 0;

  const sidebarContent = (
    <>
      <div className="rankings-sidebar-header">
        <h2 className="rankings-sidebar-title">Classements</h2>
        <span className="rankings-sidebar-mode">
          {session.compet ? 'Compétition' : 'Détente'}
        </span>
      </div>

      <div className="rankings-categories">
        {groups.map(([groupLabel, items]) => (
          <div key={groupLabel} className="rankings-group">
            <div className="rankings-group-label">{groupLabel}</div>
            {items.map((cat) => (
              <div key={cat.id}>
                <button
                  className={`rankings-category-item ${selectedId === cat.id ? 'active' : ''}`}
                  onClick={() => handleCategoryClick(cat.id)}
                >
                  <span className="rankings-category-icon">{cat.icon}</span>
                  <span className="rankings-category-name">{cat.label}</span>
                </button>
                {selectedId === cat.id && cat.subOptions && (
                  <div className="rankings-sub-options">
                    {cat.subOptions.map((opt) => (
                      <button
                        key={opt.value}
                        className={`rankings-sub-option ${currentSub === opt.value ? 'active' : ''}`}
                        onClick={() => handleSubChange(opt.value)}
                      >
                        {opt.icon && <span className="rankings-sub-icon">{opt.icon}</span>}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );

  const mainContent = (
    <main className="rankings-main">
        {/* Header */}
        <div className="rankings-main-header">
          <h2 className="rankings-main-title">
            {selected.label}
            {selected.subOptions && (() => {
              const opt = selected.subOptions!.find((o) => o.value === currentSub);
              return opt ? <span className="rankings-main-subtitle"> — {opt.label}</span> : null;
            })()}
          </h2>
          <div className="rankings-search-wrapper">
            <svg className="rankings-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
            </svg>
            <input
              type="text"
              className="rankings-search-input"
              placeholder={isTeamRanking ? 'Rechercher une équipe...' : 'Rechercher un joueur...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Table wrapper */}
        <div className={`rankings-table-scroll ${showGlissant ? 'has-glissant' : ''}`}>
          {/* Table header */}
          <div className="rankings-table-header">
            {showGlissant ? (
              <>
                <div className="rankings-row-fixed">
                  <span className="rankings-col-rank">#</span>
                  <span className="rankings-col-player">{isTeamRanking ? 'Équipe' : 'Joueur'}</span>
                  {selected.columns.map((col) => (
                    <span key={col.key} className={col.width ? 'rankings-col-secondary' : 'rankings-col-score'} style={col.width ? { width: col.width } : undefined}>
                      {col.label}
                    </span>
                  ))}
                </div>
                {diffs.map((d) => (
                  <span key={d.id} className="rankings-col-diff">{d.fin}</span>
                ))}
              </>
            ) : (
              <>
                <span className="rankings-col-rank">#</span>
                <span className="rankings-col-player">{isTeamRanking ? 'Équipe' : 'Joueur'}</span>
                {selected.columns.map((col) => (
                  <span key={col.key} className={col.width ? 'rankings-col-secondary' : 'rankings-col-score'} style={col.width ? { width: col.width } : undefined}>
                    {col.label}
                  </span>
                ))}
              </>
            )}
          </div>

          {/* Content */}
          {initialLoading && (
            <div className="rankings-loading">
              <div className="rankings-spinner" />
            </div>
          )}

          {error && !loading && (
            <div className="rankings-error">
              <span className="rankings-error-text">{error}</span>
              <button className="rankings-retry-btn" onClick={() => fetchData(0)}>Réessayer</button>
            </div>
          )}

          {!initialLoading && !error && allItems.length === 0 && !loading && (
            <div className="rankings-empty-state">
              <span className="rankings-empty-icon">🏆</span>
              <span className="rankings-empty-text">
                {debouncedSearch
                  ? `Aucun résultat pour "${debouncedSearch}"`
                  : 'Aucun classement disponible'}
              </span>
            </div>
          )}

          {allItems.length > 0 && (
            <div className="rankings-table-body" ref={tableBodyRef}>
              {allItems.map((item, idx) => {
                const displayName = isTeamRanking
                  ? (item.equipe_nom as string) || `Équipe ${item.idequipe}`
                  : item.pseudo_display || item.pseudo || '';
                const pseudoKey = isTeamRanking
                  ? String(item.idequipe || idx)
                  : (item.pseudo || '').toLowerCase();
                const isMe = !isTeamRanking && pseudoKey === myPseudo;
                const teamMatch = item.team_match as string | undefined;
                const memberMatch = item.member_match as string | undefined;
                const isSearched = !!debouncedSearch && (
                  displayName.toLowerCase().includes(debouncedSearch.toLowerCase())
                  || !!teamMatch || !!memberMatch
                );
                const rank = getRank(item, idx);

                const rankEl = rank <= 3
                  ? <span className={`rankings-medal rank-${rank}`}>{rank}</span>
                  : rank;
                const avatarEl = isTeamRanking
                  ? <PlayerAvatar pseudo={displayName} size={28} photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(displayName)}?size=56`} />
                  : <PlayerAvatar pseudo={item.pseudo || displayName} size={28} />;
                const colEls = selected.columns.map((col) => (
                  <span
                    key={col.key}
                    className={col.width ? 'rankings-col-secondary' : 'rankings-col-score'}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.format ? col.format(item[col.key], item) : String(item[col.key] ?? '-')}
                  </span>
                ));

                return (
                  <div
                    key={`${pseudoKey}-${idx}`}
                    ref={isMe ? meRowRef : undefined}
                    className={`rankings-row ${isMe ? 'is-me' : ''} ${isSearched && !isMe ? 'is-searched' : ''}`}
                  >
                    {showGlissant ? (
                      <>
                        <div className="rankings-row-fixed">
                          <span className="rankings-col-rank">{rankEl}</span>
                          <div className="rankings-col-player">
                            {avatarEl}
                            <span className="rankings-player-name-wrap">
                              <ClickablePseudo pseudo={item.pseudo || displayName} className="rankings-player-pseudo" disabled={isTeamRanking}>{displayName}</ClickablePseudo>
                              {teamMatch && <span className="rankings-match-subtitle">{teamMatch}</span>}
                              {memberMatch && <span className="rankings-match-subtitle">équipe de {memberMatch}</span>}
                            </span>
                          </div>
                          {colEls}
                        </div>
                        {item.scores && item.scores.map((s, di) => {
                          const isTop = item.top9?.[di];
                          const noScore = s === 0 || s === null;
                          return (
                            <span
                              key={di}
                              className={`rankings-col-diff ${noScore ? 'no-score' : isTop ? 'top-score' : 'low-score'}`}
                            >
                              {noScore ? '----' : fmtScore(s)}
                            </span>
                          );
                        })}
                      </>
                    ) : (
                      <>
                        <span className="rankings-col-rank">{rankEl}</span>
                        <div className="rankings-col-player">
                          {avatarEl}
                          <span className="rankings-player-name-wrap">
                            <ClickablePseudo pseudo={item.pseudo || displayName} className="rankings-player-pseudo" disabled={isTeamRanking}>{displayName}</ClickablePseudo>
                            {teamMatch && <span className="rankings-match-subtitle">{teamMatch}</span>}
                            {memberMatch && <span className="rankings-match-subtitle">équipe de {memberMatch}</span>}
                          </span>
                        </div>
                        {colEls}
                      </>
                    )}
                  </div>
                );
              })}

              {/* Sentinel for infinite scroll */}
              <div ref={sentinelRef} className="rankings-sentinel">
                {loading && !initialLoading && (
                  <div className="rankings-loading-more">
                    <div className="rankings-spinner small" />
                    <span>Chargement...</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer with total count */}
        {allItems.length > 0 && (
          <div className="rankings-footer">
            <span className="rankings-footer-info">
              {(() => {
                const unit = isTeamRanking ? 'équipe' : 'joueur';
                const unitPlural = isTeamRanking ? 'équipes' : 'joueurs';
                const shown = allItems.length;
                return totalCount > 0
                  ? `${shown} sur ${totalCount} ${totalCount > 1 ? unitPlural : unit}`
                  : `${shown} ${shown > 1 ? unitPlural : unit}`;
              })()}
            </span>
            {hasMore && loading && (
              <span className="rankings-footer-loading">Chargement...</span>
            )}
          </div>
        )}
      </main>
  );

  const compactSearchInput = (
    <div className="rankings-search-wrapper compact-search">
      <svg className="rankings-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
      </svg>
      <input
        type="text"
        className="rankings-search-input"
        placeholder={isTeamRanking ? 'Rechercher...' : 'Rechercher...'}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
    </div>
  );

  const rankingsSubLabel = selected.subOptions
    ? selected.subOptions.find(o => o.value === currentSub)?.label
    : null;

  const rankingsTitleContent = (
    <div className="back-title-info">
      <span className="back-title-name">{selected.label}</span>
      {rankingsSubLabel && (
        <span className="back-title-subtitle">{rankingsSubLabel}{totalCount > 0 ? ` · ${totalCount} ${isTeamRanking ? 'équipes' : 'joueurs'}` : ''}</span>
      )}
      {!rankingsSubLabel && totalCount > 0 && (
        <span className="back-title-subtitle">{totalCount} {isTeamRanking ? 'équipes' : 'joueurs'}</span>
      )}
    </div>
  );

  return (
    <div className="rankings-container">
      <MasterDetail
        master={sidebarContent}
        detail={mainContent}
        detailTitle={rankingsTitleContent}
        detailHeaderRight={compactSearchInput}
        hasSelection={showMobileDetail}
        onBack={() => setShowMobileDetail(false)}
      />
    </div>
  );
}
